import { z } from 'zod';

import {
  type CashDenominationAllocationDto,
  type CashDenominationAllocationRevisionDto,
  type CashDenominationBaselineType,
  type CashDenominationConfidence,
  type CashDenominationCurrentStateDto,
  type CashDenominationCurrentStateSource,
  type CashDenominationCurrentStateStatus,
  type CashDenominationRowDto,
  type CashDenominationSource,
  type CashDenominationValidationIssueCode,
  type CashDenominationValidationIssueDto,
  type CashDenominationValidationResultDto,
  type GetCashDenominationAllocationByMovementRequest,
  type GetCashDenominationAllocationResponse,
  type GetCashDenominationCurrentStateRequest,
  type GetCashDenominationCurrentStateResponse,
  type ListCashDenominationAllocationRevisionsRequest,
  type ListCashDenominationAllocationRevisionsResponse,
  type SuggestCashDenominationsRequest,
  type SuggestCashDenominationsResponse,
  cashDenominationBaselineTypeValues,
  cashDenominationConfidenceValues,
  cashDenominationCurrentStateSourceValues,
  cashDenominationCurrentStateStatusValues,
  cashDenominationSourceValues,
  cashDenominationValidationIssueCodeValues,
} from './cash-denominations.dto.js';
import { supportedCashDenominationMinorValues } from './cash-denomination-snapshots.dto.js';

const timestampSchema = z.iso.datetime();
const supportedDenominations = new Set<number>(supportedCashDenominationMinorValues);

export const CashDenominationSourceSchema: z.ZodType<CashDenominationSource> = z.enum(
  cashDenominationSourceValues,
);

export const CashDenominationConfidenceSchema: z.ZodType<CashDenominationConfidence> = z.enum(
  cashDenominationConfidenceValues,
);

export const CashDenominationCurrentStateStatusSchema: z.ZodType<CashDenominationCurrentStateStatus> =
  z.enum(cashDenominationCurrentStateStatusValues);

export const CashDenominationCurrentStateSourceSchema: z.ZodType<CashDenominationCurrentStateSource> =
  z.enum(cashDenominationCurrentStateSourceValues);

export const CashDenominationValidationIssueCodeSchema: z.ZodType<CashDenominationValidationIssueCode> =
  z.enum(cashDenominationValidationIssueCodeValues);

export const CashDenominationBaselineTypeSchema: z.ZodType<CashDenominationBaselineType> = z.enum(
  cashDenominationBaselineTypeValues,
);

export const CashDenominationRowSchema: z.ZodType<CashDenominationRowDto> = z.object({
  denom: z
    .number()
    .int()
    .positive()
    .refine((value) => supportedDenominations.has(value), 'Unsupported denomination'),
  count: z.number().int().positive(),
});

export const CashDenominationRowsSchema: z.ZodType<CashDenominationRowDto[]> = z
  .array(CashDenominationRowSchema)
  .superRefine((rows, context) => {
    addDuplicateDenominationIssues(rows, context);
  });

export const CashDenominationValidationIssueDtoSchema: z.ZodType<CashDenominationValidationIssueDto> =
  z.object({
    code: CashDenominationValidationIssueCodeSchema,
    message: z.string().trim().min(1).max(300),
    index: z.number().int().nonnegative().optional(),
    denominationMinor: z.number().optional(),
    expectedAmountMinor: z.number().optional(),
    denominationTotalMinor: z.number().optional(),
  });

export const CashDenominationValidationResultDtoSchema: z.ZodType<CashDenominationValidationResultDto> =
  z.object({
    isValid: z.boolean(),
    denominationTotalMinor: z.number(),
    issues: z.array(CashDenominationValidationIssueDtoSchema),
  });

export const SuggestCashDenominationsRequestSchema: z.ZodType<SuggestCashDenominationsRequest> =
  z.object({
    amountMinor: z.number().int().nonnegative(),
  });

export const SuggestCashDenominationsResponseSchema: z.ZodType<SuggestCashDenominationsResponse> = z
  .object({
    amountMinor: z.number().int().nonnegative(),
    rows: CashDenominationRowsSchema,
    denominationTotalMinor: z.number().int().nonnegative(),
    source: z.literal('suggested'),
    confidence: z.literal('suggested'),
    validation: CashDenominationValidationResultDtoSchema,
  })
  .superRefine((value, context) => {
    addRowsTotalMismatchIssue(value.rows, value.denominationTotalMinor, context, [
      'denominationTotalMinor',
    ]);
    addRowsTotalMismatchIssue(value.rows, value.amountMinor, context, ['amountMinor']);
  });

export const CashDenominationAllocationDtoSchema: z.ZodType<CashDenominationAllocationDto> = z
  .object({
    id: z.uuid(),
    companyId: z.uuid(),
    cashMovementId: z.uuid(),
    cashLocationId: z.uuid(),
    movementAmountMinor: z.number().int().nonnegative(),
    currentRevisionId: z.uuid(),
    currentRows: CashDenominationRowsSchema,
    source: CashDenominationSourceSchema,
    confidence: CashDenominationConfidenceSchema,
    purchaseId: z.uuid().nullable().optional(),
    effectiveAt: timestampSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .superRefine((value, context) => {
    addRowsTotalMismatchIssue(value.currentRows, value.movementAmountMinor, context, [
      'movementAmountMinor',
    ]);
    addSourceConfidenceMismatchIssue(value.source, value.confidence, context);
  });

export const CashDenominationAllocationRevisionDtoSchema: z.ZodType<CashDenominationAllocationRevisionDto> =
  z
    .object({
      id: z.uuid(),
      companyId: z.uuid(),
      allocationId: z.uuid(),
      cashMovementId: z.uuid(),
      source: CashDenominationSourceSchema,
      confidence: CashDenominationConfidenceSchema,
      rows: CashDenominationRowsSchema,
      denominationTotalMinor: z.number().int().nonnegative(),
      note: z.string().trim().min(1).max(500).nullable(),
      createdByUserId: z.uuid().nullable(),
      effectiveAt: timestampSchema,
      createdAt: timestampSchema,
    })
    .superRefine((value, context) => {
      addRowsTotalMismatchIssue(value.rows, value.denominationTotalMinor, context, [
        'denominationTotalMinor',
      ]);
      addSourceConfidenceMismatchIssue(value.source, value.confidence, context);
    });

export const GetCashDenominationAllocationByMovementRequestSchema: z.ZodType<GetCashDenominationAllocationByMovementRequest> =
  z.object({
    companyId: z.uuid(),
    cashMovementId: z.uuid(),
  });

export const GetCashDenominationAllocationResponseSchema: z.ZodType<GetCashDenominationAllocationResponse> =
  z.object({
    allocation: CashDenominationAllocationDtoSchema,
  });

export const ListCashDenominationAllocationRevisionsRequestSchema: z.ZodType<ListCashDenominationAllocationRevisionsRequest> =
  z.object({
    companyId: z.uuid(),
    allocationId: z.uuid(),
  });

export const ListCashDenominationAllocationRevisionsResponseSchema: z.ZodType<ListCashDenominationAllocationRevisionsResponse> =
  z.object({
    revisions: z.array(CashDenominationAllocationRevisionDtoSchema),
  });

export const CashDenominationCurrentStateDtoSchema: z.ZodType<CashDenominationCurrentStateDto> = z
  .object({
    id: z.uuid().nullable(),
    companyId: z.uuid(),
    storeId: z.uuid().nullable(),
    locationId: z.uuid().nullable(),
    stationId: z.uuid().nullable(),
    cashLocationId: z.uuid(),
    status: CashDenominationCurrentStateStatusSchema,
    confidence: CashDenominationCurrentStateStatusSchema,
    source: CashDenominationCurrentStateSourceSchema,
    rows: CashDenominationRowsSchema,
    totalMinor: z.number().int().nonnegative().nullable(),
    ledgerBalanceMinor: z.number().int(),
    differenceMinor: z.number().int().nullable(),
    asOf: timestampSchema.nullable(),
    cashSessionId: z.uuid().nullable(),
    lastMovementId: z.uuid().nullable(),
    sourceSnapshotId: z.uuid().nullable(),
    sourceBaselineId: z.uuid().nullable(),
    stale: z.boolean(),
    note: z.string().trim().min(1).max(500),
    createdAt: timestampSchema.nullable(),
    updatedAt: timestampSchema.nullable(),
  })
  .superRefine((value, context) => {
    if (value.status !== value.confidence) {
      context.addIssue({
        code: 'custom',
        message: 'Current state status and confidence must match.',
        path: ['confidence'],
      });
    }

    if (value.status === 'unknown') {
      if (value.rows.length > 0) {
        context.addIssue({
          code: 'custom',
          message: 'Unknown current states must not include denomination rows.',
          path: ['rows'],
        });
      }
      if (value.totalMinor !== null) {
        context.addIssue({
          code: 'custom',
          message: 'Unknown current states must not include a denomination total.',
          path: ['totalMinor'],
        });
      }
      return;
    }

    if (value.totalMinor === null) {
      context.addIssue({
        code: 'custom',
        message: 'Trusted current states require a denomination total.',
        path: ['totalMinor'],
      });
      return;
    }

    addRowsTotalMismatchIssue(value.rows, value.totalMinor, context, ['totalMinor']);

    if (value.status === 'counted' && value.differenceMinor !== 0) {
      context.addIssue({
        code: 'custom',
        message: 'Counted current states must have zero difference.',
        path: ['differenceMinor'],
      });
    }
  });

export const GetCashDenominationCurrentStateRequestSchema: z.ZodType<GetCashDenominationCurrentStateRequest> =
  z.object({
    companyId: z.uuid(),
    cashLocationId: z.uuid(),
  });

export const GetCashDenominationCurrentStateResponseSchema: z.ZodType<GetCashDenominationCurrentStateResponse> =
  z.object({
    currentState: CashDenominationCurrentStateDtoSchema,
  });

function calculateRowsTotalMinor(rows: readonly CashDenominationRowDto[]): number {
  return rows.reduce((total, row) => total + row.denom * row.count, 0);
}

function addDuplicateDenominationIssues(
  rows: readonly CashDenominationRowDto[],
  context: z.RefinementCtx,
): void {
  const seen = new Set<number>();
  for (const [index, row] of rows.entries()) {
    if (seen.has(row.denom)) {
      context.addIssue({
        code: 'custom',
        message: 'Duplicate denomination rows are not supported.',
        path: [index, 'denom'],
      });
      continue;
    }
    seen.add(row.denom);
  }
}

function addRowsTotalMismatchIssue(
  rows: readonly CashDenominationRowDto[],
  expectedAmountMinor: number,
  context: z.RefinementCtx,
  path: (string | number)[],
): void {
  const denominationTotalMinor = calculateRowsTotalMinor(rows);
  if (denominationTotalMinor === expectedAmountMinor) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Denomination total does not match the expected amount.',
    path,
  });
}

function addSourceConfidenceMismatchIssue(
  source: CashDenominationSource,
  confidence: CashDenominationConfidence,
  context: z.RefinementCtx,
): void {
  if (source === confidence) {
    return;
  }

  context.addIssue({
    code: 'custom',
    message: 'Allocation source and confidence must match.',
    path: ['confidence'],
  });
}
